import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, from, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Consultant } from 'src/app/models/consultant.model';
import { Jobtype, QuestionGroup } from 'src/app/models/jobtype.model';
import { Pagination } from 'src/app/models/pagination.model';
import { Process, ProcessEdit } from 'src/app/models/process.model';
import { Profile } from 'src/app/models/profile.model';
import { fromConsultant } from 'src/app/reducers/consultant.reducer';
import { fromJobtypes } from 'src/app/reducers/jobtypes.reducer';
import { fromProfiles } from 'src/app/reducers/profiles.reducer';
import { UtilsService } from 'src/app/services/utils.service';
import { IdealProfileService } from 'src/app/services/ideal-profile.service';
import { JobtypeService } from 'src/app/services/jobtype.service';
import { Location } from '@angular/common';
import { AppConfig } from 'src/app/helpers/app.config';
import { Project } from 'src/app/models/project';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ct-ideal-profile-detail',
  templateUrl: './ideal-profile-detail.component.html',
  styleUrls: ['./ideal-profile-detail.component.scss']
})
export class IdealProfileDetailComponent implements OnInit, OnDestroy {

  jobtypes$: Observable<Jobtype[]>;
  consultant$: Observable<Consultant>;
  profiles$: Observable<Pagination<Profile>>;

  profileConsultantSubscription: Subscription;

  action: string;

  jobtypeM: number;
  profileId: number;
  profileSelected: Profile;

  jobtypeSelected: Jobtype;
  currentConsultant: Consultant;
  officeId: number;

  HCC: number[];
  projects: Project[] = [];

  editProfileForm: FormGroup = this.fb.group({
    name: ['', Validators.required],
    description: [''],
    jobtype: ['', Validators.required],
    project: [null],
  });

  constructor(
    private store: Store,
    private router: Router,
    private fb: FormBuilder,
    public utilsService: UtilsService,
    private idealProfileService: IdealProfileService,
    private activatedRoute: ActivatedRoute,
    public utils: UtilsService,
    public jobtypeService: JobtypeService,
    private location: Location,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    if (this.router.url.includes('detail')) {
      this.action = 'DETAIL';
      this.setEditProfile(false);
    } else if (this.router.url.includes('edit')) {
      this.action = 'EDIT';
      this.setEditProfile(true);
    } else if (this.router.url.includes('create')) {
      this.action = 'CREATE';
      this.setEditProfile(true);
    }

    // Cargamos las oficinas que por configuración, necesitan ver los clientes.
    this.HCC = AppConfig.settings.OFFICES_WITH_PROJECTS.split(',').map(officeId => Number(officeId));

    this.profileId = Number(this.activatedRoute.snapshot.paramMap.get('profileId'));

    this.jobtypes$ = this.store.pipe(select(fromJobtypes.selectBusinessJobtypes));
    this.profiles$ = this.store.pipe(select(fromProfiles.selectProfilesState));
    this.consultant$ = this.store.pipe(select(fromConsultant.selectConsultantState));

    if ((this.action === 'DETAIL') || (this.action === 'EDIT')) {
      this.profileConsultantSubscription = combineLatest([
        this.profiles$,
        this.consultant$,
      ]).subscribe(([profiles, consultant]) => {
        // Si la oficina cambia volver al listado de perfiles ideales
        if (!this.officeId) {
          this.officeId = consultant.office.id;
        }
        if (this.officeId !== consultant.office.id) {
          this.router.navigate(['consultant/profiles']);
        }

        this.currentConsultant = consultant;
        // obtenemos cual es el perfil ideal que queremos ver/editar
        this.profileSelected = profiles.data.find(profile => profile.id === this.profileId);

        // en caso de necesitar los clientes para la oficina, llamamos
        if (this.HCC.includes(consultant.office.randstadOffice)) {
          this.projects = consultant.projects;
        }

        // controlammos si ese consultor puede editar ese perfil ideal
        if (this.action === 'EDIT') {
          if (this.profileSelected.isUb) {
            this.router.navigate(['consultant/profiles/detail/' + this.profileSelected.id]);
          } else if (this.profileSelected.officeId != consultant.office.id.toString()) {
            this.router.navigate(['consultant/profiles']);
          }
        }

        // Rellenamos los campos con los datos que corresponda
        this.editProfileForm.get('name').setValue(this.profileSelected.name);
        this.editProfileForm.get('description').setValue(this.profileSelected.description);
        this.editProfileForm.get('jobtype').setValue(this.profileSelected.jobtypeId);

      });
    } else if (this.action === 'CREATE') {
      this.profileConsultantSubscription = this.store.pipe(select(fromConsultant.selectConsultantState)).subscribe(consultant => {
        this.currentConsultant = consultant;
        this.jobtypes$.subscribe(res => {
          this.editProfileForm.get('jobtype').setValue(null);
        });
      }, error => {
        this.utilsService.toast('error', 'CannotGetJobtypes', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + ' /jobtypes', body: { companyTypeId: this.currentConsultant.office.businessAreaIds }, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } }));
      });
    }
  }

  setJobtypeSelected() {
    this.jobtypes$.subscribe(res => {
      this.jobtypeSelected = res.find(jobtype => jobtype.id === this.editProfileForm.controls.jobtype.value);
    })
  }

  onSubmit() {
    const inputSkillsArray = Array.from(document.getElementsByClassName('input-edit'));
    let skillsToSend: any = {};
    let profileToSend;

    inputSkillsArray.map((inputSkill: any) => {
      const splitItem = inputSkill.id.split('_');
      skillsToSend[splitItem[0]] = Number(inputSkill.value)
    });

    // filtramos las que tienen valor
    Object.keys(skillsToSend).map(questionGroup => skillsToSend[questionGroup] == 0 ? delete skillsToSend[questionGroup] : null)


    if (!Object.keys(skillsToSend).length) {
      this.utilsService.toast('warning', 'NeedsMinimumScore');
    } else {
      if (this.action === 'EDIT') {
        profileToSend = {
          name: this.editProfileForm.controls.name.value,
          description: this.editProfileForm.controls.description.value,
          jobtypeId: this.profileSelected.jobtypeId,
          testModelId: this.profileSelected.testModelId,
          officeId: this.profileSelected.officeId,
          skills: skillsToSend,
          clientId: this.currentConsultant.client.id
        };

        this.idealProfileService.edit(this.profileSelected.id, profileToSend).subscribe(
          result => {
            this.utilsService.toast('success', 'IdealProfileEdited');
            // this.router.navigate(['consultant/profiles']);
            this.location.back();
          },
          error => this.utilsService.toast('error', 'CannotEditIdealProfile', true, this.utilsService.getNacsData({ endpoint: 'GET' + AppConfig.settings.API + ' /v2/idealProfiles/' + this.profileSelected.id, body: profileToSend, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } }))
        );

      } else if (this.action === 'CREATE') {
        profileToSend = {
          name: this.editProfileForm.controls.name.value,
          description: this.editProfileForm.controls.description.value,
          jobtypeId: this.jobtypeSelected.id,
          testModelId: this.jobtypeSelected.testModelId,
          officeId: this.currentConsultant.office.id,
          skills: skillsToSend,
          clientId: this.currentConsultant.client.id
        };


        this.idealProfileService.create(profileToSend).subscribe(
          result => {
            this.utilsService.toast('success', 'IdealProfileCreated');
            // this.router.navigate(['consultant/profiles']);
            this.location.back();
          },
          error => this.utilsService.toast('error', 'CannotCreateIdealProfile', true, this.utilsService.getNacsData({ endpoint: 'POST ' + AppConfig.settings.API + '/idealProfiles/', body: profileToSend, error: { errorType: error.errorType, errorCode: error.errorCode, message: error.message } }))
        );
      }
    }

  }

  valueCheck(object) {
    if (Number(object.value) > Number(object.max)) {
      object.value = object.max;
    }
    if (Number(object.value) < Number(object.min)) {
      object.value = object.min;
    }
  }

  // Evitamos el warning que daba por utilizar en el FormGroup directamente la propiedad disabled en html.
  setEditProfile(canEditProfile: boolean): void {
    if (!canEditProfile) {
      this.editProfileForm.get('name').disable();
      this.editProfileForm.get('description').disable();
      this.editProfileForm.get('jobtype').disable();
    } else if (this.action === 'EDIT') {
      this.editProfileForm.get('jobtype').disable();
    }
  }

  // Obtenemos los datos de competencias personalizadas del cliente, por proyecto seleccionado. Y refrescamos los nombres de las competencias.
  getClientSkills(): void {
    if(this.editProfileForm.get('project').value !== 'null'){
      this.translateService.use('projects__' + this.editProfileForm.get('project').value + '__es-ES');
    } else {
      this.translateService.use(this.currentConsultant?.client.defaultLanguage ?? 'es-ES');
    }
  }

  ngOnDestroy(): void {
    this.profileConsultantSubscription.unsubscribe();
    // volvemos a poner el idioma español por si se ha obtenido las traducciónes de algun liente
    this.translateService.use(this.currentConsultant?.client.defaultLanguage ?? 'es-ES');
  }

}
